import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../services/api';

const toDateInputValue = (date) => date.toISOString().slice(0, 10);
const formatEventType = (value) => (value || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const OpsSupportAudit = () => {
  const { orgId } = useParams();
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [actors, setActors] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 365);
    return {
      start_date: toDateInputValue(start),
      end_date: toDateInputValue(end),
      actor_user_id: '',
      target_user_id: '',
      event_type: '',
      search: '',
    };
  });

  const actorOptions = useMemo(() => {
    return (actors || [])
      .filter((user) => user?.id)
      .map((user) => ({
        id: user.id,
        label: user.full_name ? `${user.full_name} (${user.email})` : user.email,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [actors]);

  const userOptions = useMemo(() => {
    return (users || [])
      .filter((user) => user?.id)
      .map((user) => ({
        id: user.id,
        label: user.full_name ? `${user.full_name} (${user.email})` : user.email,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users]);

  const loadEventTypes = async () => {
    try {
      const response = await api.get(`/acct-mgmt/v1/orgs/${orgId}/ops-audit/filters`);
      setEventTypes(response.data?.event_types || []);
      setActors(response.data?.actors || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load audit filters.');
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get(`/acct-mgmt/v1/orgs/${orgId}/users`);
      setUsers(response.data?.users || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users.');
    }
  };

  const loadEvents = async (nextFilters = filters, nextPage = page, nextPageSize = pageSize) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/acct-mgmt/v1/orgs/${orgId}/ops-audit`, {
        params: { ...nextFilters, page: nextPage, page_size: nextPageSize },
      });
      setEvents(response.data?.events || []);
      setTotal(response.data?.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load support audit history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!orgId) return;
    loadEventTypes();
    loadUsers();
    loadEvents(filters, page, pageSize);
  }, [orgId]);

  const toggleExpanded = (eventId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const formatMetadataEntries = (metadata) => {
    if (!metadata || typeof metadata !== 'object') return [];
    if (metadata.event_type === 'trial_support_updated' || metadata.__eventType === 'trial_support_updated') {
      const formatTrialEnd = (value) => {
        if (!value) return '—';
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString(undefined, { timeZone: 'UTC' });
      };
      const formatLimit = (value, unlimited) => (unlimited || value == null ? 'Unlimited' : Number(value).toLocaleString());
      const buildSettingsLine = ({
        status,
        trialEnd,
        requestLimit,
        unlimitedRequests,
        datasetAvailability,
        derivedData,
        historicAvailability,
        excelAccess,
      }) => (
        `Status: ${formatEventType(status || 'trialing')}; ` +
        `Trial Access Through: ${formatTrialEnd(trialEnd)}; ` +
        `Monthly Request Limit: ${formatLimit(requestLimit, unlimitedRequests)}; ` +
        `Dataset Availability: ${datasetAvailability || 'All Data Sources'}; ` +
        `Derived Data: ${derivedData ? 'Enabled' : 'Disabled'}; ` +
        `Historic Availability: ${historicAvailability == null ? '—' : Number(historicAvailability).toLocaleString()}; ` +
        `Excel Access: ${excelAccess ? 'Enabled' : 'Disabled'}`
      );
      return [
        {
          label: 'Previous Settings',
          value: buildSettingsLine({
            status: metadata.previous_status,
            trialEnd: metadata.previous_trial_end,
            requestLimit: metadata.previous_monthly_request_limit,
            unlimitedRequests: metadata.previous_unlimited_requests,
            datasetAvailability: metadata.previous_dataset_availability,
            derivedData: metadata.previous_derived_data,
            historicAvailability: metadata.previous_historic_availability,
            excelAccess: metadata.previous_excel_access,
          }),
        },
        {
          label: 'Current Settings',
          value: buildSettingsLine({
            status: metadata.updated_status,
            trialEnd: metadata.updated_trial_end,
            requestLimit: metadata.monthly_request_limit,
            unlimitedRequests: metadata.unlimited_requests,
            datasetAvailability: metadata.dataset_availability,
            derivedData: metadata.derived_data,
            historicAvailability: metadata.historic_availability,
            excelAccess: metadata.excel_access,
          }),
        },
      ];
    }
    return Object.entries(metadata).map(([key, value]) => {
      let displayValue = value;
      const isDateOnlyField = key === 'access_ends_on' || key === 'next_renewal_on' || key === 'effective_on';
      if (Array.isArray(displayValue)) {
        displayValue = displayValue.join(', ');
      } else if (typeof displayValue === 'object') {
        displayValue = JSON.stringify(displayValue);
      } else if (isDateOnlyField && displayValue) {
        const parsed = new Date(displayValue);
        if (!Number.isNaN(parsed.getTime())) {
          if (
            key === 'access_ends_on' &&
            parsed.getUTCHours() === 0 &&
            parsed.getUTCMinutes() === 0 &&
            parsed.getUTCSeconds() === 0 &&
            parsed.getUTCMilliseconds() === 0
          ) {
            parsed.setUTCDate(parsed.getUTCDate() - 1);
          }
          displayValue = parsed.toLocaleDateString(undefined, { timeZone: 'UTC' });
        }
      } else if (['from_plan_code', 'to_plan_code', 'billing_cadence', 'from_billing_cadence', 'to_billing_cadence'].includes(key) && typeof displayValue === 'string') {
        displayValue = formatEventType(displayValue);
      }
      const label = key === 'access_ends_on'
        ? 'Access Through'
        : key === 'effective_on'
          ? 'Scheduled Effective Date'
          : formatEventType(key);
      return { label, value: displayValue };
    });
  };

  const handleChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleApply = (event) => {
    event.preventDefault();
    setPage(1);
    loadEvents(filters, 1, pageSize);
  };

  const handleReset = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 365);
    const next = {
      start_date: toDateInputValue(start),
      end_date: toDateInputValue(end),
      actor_user_id: '',
      target_user_id: '',
      event_type: '',
      search: '',
    };
    setFilters(next);
    setPage(1);
    loadEvents(next, 1, pageSize);
  };

  const handleExport = async () => {
    try {
      const response = await api.get(`/acct-mgmt/v1/orgs/${orgId}/ops-audit`, {
        params: { ...filters, page, page_size: pageSize, export: 'csv' },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'support-audit-history.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to export support audit history.');
    }
  };

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);
  const handlePageChange = (nextPage) => {
    const clamped = Math.min(Math.max(nextPage, 1), totalPages);
    setPage(clamped);
    loadEvents(filters, clamped, pageSize);
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="h4 fw-bold mb-1">Support Audit Trail</h2>
          <div className="text-muted">Review support actions taken for this organization.</div>
        </div>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Filters</h5>
          <form className="row g-3 align-items-start" onSubmit={handleApply}>
            <div className="col-12 col-md-6">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-control"
                value={filters.search}
                onChange={handleChange('search')}
                placeholder="Summary, event, actor, target"
              />
              <div className="form-text">Enter at least 3 characters.</div>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.start_date}
                onChange={handleChange('start_date')}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.end_date}
                onChange={handleChange('end_date')}
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Actor</label>
              <select
                className="form-select"
                value={filters.actor_user_id}
                onChange={handleChange('actor_user_id')}
              >
                <option value="">All Users</option>
                {actorOptions.map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Target User</label>
              <select
                className="form-select"
                value={filters.target_user_id}
                onChange={handleChange('target_user_id')}
              >
                <option value="">All Targets</option>
                {userOptions.map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Event Type</label>
              <select
                className="form-select"
                value={filters.event_type}
                onChange={handleChange('event_type')}
              >
                <option value="">All Events</option>
                {eventTypes.map((type) => (
                  <option key={type} value={type}>{formatEventType(type)}</option>
                ))}
              </select>
            </div>
            <div className="col-12 d-flex gap-2">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                Apply Filters
              </button>
              <button type="button" className="btn btn-outline-primary" onClick={handleReset} disabled={loading}>
                Reset
              </button>
              <button type="button" className="btn btn-outline-secondary ms-auto" onClick={handleExport}>
                Export CSV
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h5 className="card-title">Support Audit Trail</h5>
          {loading ? (
            <div className="text-muted">Loading support audit history...</div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>Date/Time</th>
                    <th>Actor</th>
                    <th>Event</th>
                    <th>User</th>
                    <th>Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => {
                    const isExpanded = expanded.has(event.id);
                    const metadataEntries = formatMetadataEntries({ ...(event.metadata || {}), __eventType: event.event_type });
                    return (
                      <React.Fragment key={event.id}>
                        <tr
                          role={metadataEntries.length > 0 ? 'button' : undefined}
                          tabIndex={metadataEntries.length > 0 ? 0 : -1}
                          onClick={() => {
                            if (metadataEntries.length > 0) toggleExpanded(event.id);
                          }}
                          onKeyDown={(e) => {
                            if (metadataEntries.length === 0) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleExpanded(event.id);
                            }
                          }}
                          style={metadataEntries.length > 0 ? { cursor: 'pointer' } : undefined}
                        >
                          <td>{new Date(event.created_at).toLocaleString()}</td>
                          <td>{event.actor_display || event.metadata?.actor || event.actor_name || event.actor_email || 'System'}</td>
                          <td className="text-nowrap">
                            <button
                              type="button"
                              className="btn btn-link p-0 me-2"
                              aria-label={isExpanded ? 'Hide details' : 'Show details'}
                              onClick={() => toggleExpanded(event.id)}
                              disabled={metadataEntries.length === 0}
                              title={metadataEntries.length === 0 ? 'No details' : (isExpanded ? 'Hide details' : 'Show details')}
                            >
                              <i className={`fas ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} aria-hidden="true"></i>
                            </button>
                            {formatEventType(event.event_type)}
                          </td>
                          <td>{event.target_name || event.target_email || '-'}</td>
                          <td>{event.summary || '-'}</td>
                        </tr>
                        {isExpanded && metadataEntries.length > 0 && (
                          <tr className="table-light">
                            <td colSpan={5}>
                              <div className="small">
                                {(event.actor_name || event.actor_email || event.actor_display || event.metadata?.actor) && (
                                  <div className="d-flex flex-wrap gap-2">
                                    <div className="fw-semibold">Support Rep:</div>
                                    <div>
                                      {event.actor_display
                                        || event.metadata?.actor
                                        || (event.actor_name
                                          ? `${event.actor_name}${event.actor_email ? ` (${event.actor_email})` : ''}`
                                          : event.actor_email)}
                                    </div>
                                  </div>
                                )}
                                {metadataEntries.map((entry) => (
                                  <div key={entry.label} className="d-flex flex-wrap gap-2">
                                    <div className="fw-semibold">{entry.label}:</div>
                                    <div>{entry.value || '—'}</div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {events.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-muted text-center">No audit events found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted small">
              {total === 0 ? 'Showing 0 of 0' : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}
            </div>
            <div className="btn-group">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpsSupportAudit;
